import aj3 from "@/assets/socialProof/aj3.png";
import boras from "@/assets/socialProof/boras.png";
import dux from "@/assets/socialProof/dux.png";
import liquid from "@/assets/socialProof/liquid.png";
import saf from "@/assets/socialProof/saf.png";
import tekkz from "@/assets/socialProof/tekkz.png";
import { cn } from "@/lib/utils";
import { useTranslation } from "@repo/shared/i18n";
import { Fragment, useLayoutEffect, useRef, useState } from "react";

/*
  The trust strip under the launch banner: the pros, teams and creators who
  use EasySBC. Static content — no links, no hover, no API.
*/

export type SocialProofName =
  | "Tekkz"
  | "Team Liquid"
  | "DUX"
  | "SAF"
  | "BorasLegend"
  | "AJ3";

type Member = {
  /** Shown next to the avatar. Never translated: they are proper nouns. */
  name: SocialProofName;
  /** Full name, for people who can't see the avatar. */
  alt: string;
  src: string;
  /*
    Crests are letterboxed on a disc so the circular mask doesn't crop them;
    photos fill it.
  */
  crest?: "light" | "dark";
};

const ROSTER: Member[] = [
  { name: "Tekkz", alt: "Tekkz", src: tekkz },
  { name: "Team Liquid", alt: "Team Liquid", src: liquid, crest: "light" },
  { name: "DUX", alt: "Team DUX", src: dux, crest: "light" },
  { name: "SAF", alt: "SAF Esports", src: saf, crest: "dark" },
  { name: "BorasLegend", alt: "BorasLegend", src: boras },
  { name: "AJ3", alt: "AJ3", src: aj3 },
];

const Divider = () => (
  <span aria-hidden className="h-3.5 w-px shrink-0 bg-white/12" />
);

/* One unit so a wrap never leaves the avatar on the line above its name. */
const Entry = ({ name, alt, src, crest }: Member) => (
  <span className="flex shrink-0 items-center gap-2">
    <img
      src={src}
      alt={alt}
      loading="lazy"
      width={22}
      height={22}
      className={cn(
        "size-[22px] shrink-0 rounded-full",
        crest ? "object-contain" : "object-cover",
        crest === "light" && "bg-white",
        crest === "dark" && "bg-[#1b2430]",
      )}
    />
    <span className="font-sans text-[12.5px] font-semibold leading-none text-[#8a8a8a]">
      {name}
    </span>
  </span>
);

/**
 * The roster on one line: everything renders, whatever wrapped onto a second
 * line is dropped, and "+ more" follows the last name that fit. Re-measures
 * from the full roster whenever the width changes.
 */
const FittedRoster = ({
  members,
  more,
}: {
  members: Member[];
  more: string;
}) => {
  const rowRef = useRef<HTMLDivElement>(null);
  const [count, setCount] = useState(members.length);
  const [width, setWidth] = useState(0);

  useLayoutEffect(() => {
    const row = rowRef.current;
    if (!row?.parentElement) return;
    const observer = new ResizeObserver(([entry]) =>
      setWidth(Math.round(entry.contentRect.width)),
    );
    observer.observe(row.parentElement);
    return () => observer.disconnect();
  }, []);

  // Show everyone on each width change, then trim to what fit on line one.
  useLayoutEffect(() => setCount(members.length), [width, members.length]);
  useLayoutEffect(() => {
    const items = Array.from(rowRef.current?.children ?? []) as HTMLElement[];
    if (count !== members.length || !items.length) return;
    const top = items[0].offsetTop;
    const fitting = items.filter((el) => el.offsetTop === top).length;
    if (fitting < count) setCount(fitting);
  }, [count, width, members.length]);

  return (
    <div className="flex w-full min-w-0 items-center gap-2">
      <div
        ref={rowRef}
        className="flex h-[22px] min-w-0 flex-wrap items-center gap-2 overflow-hidden"
      >
        {members.slice(0, count).map((member, i) => (
          <span key={member.name} className="flex shrink-0 items-center gap-2">
            {i > 0 && <Divider />}
            <Entry {...member} />
          </span>
        ))}
      </div>
      <span className="shrink-0 font-sans text-[12.5px] leading-none text-[#737373]">
        {more}
      </span>
    </div>
  );
};

type Props = {
  className?: string;
  /** Who to show and in what order; the full roster when omitted. */
  members?: SocialProofName[];
  /** Overrides the launch banner's label. */
  label?: string;
  /** Label on its own line, roster kept to one line below it. */
  fitOneLine?: boolean;
};

export const SocialProofStrip = ({
  className,
  members,
  label,
  fitOneLine,
}: Props) => {
  const { t } = useTranslation("clubImport");
  const roster = members
    ? members.map((name) => ROSTER.find((m) => m.name === name)!)
    : ROSTER;
  const more = t("launchBanner.socialProof.more");
  return (
    <div
      className={cn(
        "relative flex flex-wrap items-center gap-x-3 gap-y-2.5",
        "border-t border-white/7 bg-white/2 px-[26px] py-3",
        className,
      )}
    >
      <span className="font-oswald text-[11px] uppercase leading-none tracking-[0.13em] text-[#8a8a8a]">
        {label ?? t("launchBanner.socialProof.label")}
      </span>
      {fitOneLine ? (
        <FittedRoster members={roster} more={more} />
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          {roster.map((member, i) => (
            <Fragment key={member.name}>
              {i > 0 && <Divider />}
              <Entry {...member} />
            </Fragment>
          ))}
          <span className="font-sans text-[12.5px] leading-none text-[#737373]">
            {more}
          </span>
        </div>
      )}
    </div>
  );
};
